import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatINR } from "@/lib/api";
import { X, FileSpreadsheet, Loader2, ExternalLink } from "lucide-react";
import DocLinesDialog from "@/components/DocLinesDialog";

const fmtD = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const AGE_BRACKETS = [["b0_30", "0–30 d"], ["b31_60", "31–60 d"], ["b61_90", "61–90 d"], ["b91_180", "91–180 d"], ["b181_365", "181–365 d"], ["b365_plus", "365+ d"], ["non_ppi", "Non-PPI"]];

export default function ItemMovementsDialog({ item, fromMonth, toMonth, onClose }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("mov");
  const [gl, setGl] = useState(null);
  const [glErr, setGlErr] = useState("");
  const [age, setAge] = useState(null);
  const [ageErr, setAgeErr] = useState("");
  const [exporting, setExporting] = useState(false);
  const [docDrill, setDocDrill] = useState(null);

  useEffect(() => {
    setD(null); setErr(""); setGl(null); setGlErr(""); setAge(null); setAgeErr(""); setView("mov");
    if (!item) return;
    api.get("/stock/item-movements", { params: { material: item.material, plant: item.plant || "", from_month: fromMonth || "", to_month: toMonth || "" } })
      .then(({ data }) => setD(data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [item, fromMonth, toMonth]);

  useEffect(() => {
    if (!item || view !== "age" || age || ageErr) return;
    api.get("/stock/item-ageing", { params: { material: item.material, plant: item.plant || "" } })
      .then(({ data }) => setAge(data))
      .catch((e) => setAgeErr(e.response?.data?.detail || e.message));
  }, [item, view, age, ageErr]);

  useEffect(() => {
    if (!item || view !== "gl" || gl || glErr) return;
    api.get("/stock/gl-entries", { params: { material: item.material, from_month: fromMonth || "", to_month: toMonth || "" } })
      .then(({ data }) => setGl(data))
      .catch((e) => setGlErr(e.response?.data?.detail || e.message));
  }, [item, view, gl, glErr, fromMonth, toMonth]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e) => { if (e.key === "Escape" && !docDrill) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose, docDrill]);

  if (!item) return null;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const q = new URLSearchParams({ material: item.material, from_month: fromMonth || "", to_month: toMonth || "" });
      let url, name;
      if (view === "gl") {
        url = `${API}/stock/gl-entries/export?${q}`; name = `DeePiping_GL_${item.material}.xlsx`;
      } else {
        q.set("plant", item.plant || "");
        url = `${API}/stock/item-movements/export?${q}`; name = `DeePiping_movements_${item.material}.xlsx`;
      }
      const res = await fetch(url, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = name; a.click();
    } finally { setExporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="item-movements-dialog" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold text-[#B01E24]" data-testid="drill-material">{item.material}</span>
              {item.plant && <span className="text-xs text-slate-500">Plant {item.plant}</span>}
              {d && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${d.source === "SAP" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : d.source === "UPLOAD" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-amber-50 text-amber-700 border-amber-300"}`} data-testid="drill-source-chip">
                  {d.source === "SAP" ? "SAP Live" : d.source === "UPLOAD" ? "Uploaded MB51" : "Mock"}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{item.description && item.description !== item.material ? item.description : "All movements for this item"}{fromMonth ? ` · ${fromMonth} – ${toMonth || fromMonth}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportExcel} disabled={!d || exporting || view === "age"} data-testid="drill-export-excel" className="flex items-center gap-1.5 text-xs bg-emerald-700/90 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-md px-3 py-1.5 transition-colors">
              {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel
            </button>
            <button onClick={onClose} data-testid="drill-close-button" className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 transition-colors"><X size={16} /></button>
          </div>
        </div>

        <div className="px-5 pt-2 flex gap-1 border-b border-slate-200">
          {[["mov", "Movements (MB51)"], ["age", "Ageing"], ["gl", "GL Entries (SAP)"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`drill-view-${k}`}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${view === k ? "border-[#B01E24] text-[#B01E24]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>

        <div className="overflow-auto report-scroll flex-1">
          {view === "age" ? (
            <>
              {ageErr && <div className="m-4 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="age-error">{ageErr}</div>}
              {!age && !ageErr && <div className="p-8 text-sm text-slate-400 animate-pulse text-center">Loading ageing…</div>}
              {age && age.rows.length === 0 && <div className="p-8 text-sm text-slate-400 text-center" data-testid="age-empty">No positive closing stock to age for this item.</div>}
              {age && age.rows.length > 0 && (
                <div className="p-4 space-y-4" data-testid="age-panel">
                  {age.rows.map((r, i) => (
                    <div key={i} className="rounded-lg border border-slate-200">
                      <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-slate-100 bg-slate-50 text-xs">
                        <span className="font-semibold text-slate-700">Plant {r.plant || "—"}</span>
                        <span>Qty <span className="font-mono">{r.qty?.toLocaleString("en-IN")}</span> {r.uom}</span>
                        <span>Value <span className="font-mono font-semibold">{formatINR(r.value)}</span></span>
                        <span className="text-slate-400">Last receipt {fmtD(r.last_receipt)}</span>
                      </div>
                      <table className="w-full text-[12px]" data-testid={`age-brackets-${i}`}>
                        <thead><tr className="border-b border-slate-200">
                          {AGE_BRACKETS.map(([k, l]) => <th key={k} className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-400">{l}</th>)}
                        </tr></thead>
                        <tbody><tr>
                          {AGE_BRACKETS.map(([k]) => (
                            <td key={k} className={`px-2 py-2 text-right font-mono ${r[k] ? "text-slate-800" : "text-slate-300"}`}>{r[k] ? formatINR(r[k]) : "—"}</td>
                          ))}
                        </tr></tbody>
                      </table>
                      {(r.receipts || []).length > 0 && (
                        <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500">
                          Recent receipts: {r.receipts.slice(0, 6).map((rc) => `${fmtD(rc.date)} (${rc.qty?.toLocaleString("en-IN")})`).join(" · ")}
                        </div>
                      )}
                    </div>
                  ))}
                  <button onClick={() => { onClose(); navigate(`/reports/stock-ageing?q=${encodeURIComponent(item.material)}`); }}
                    data-testid="age-open-full" className="flex items-center gap-1.5 text-xs text-[#B01E24] hover:underline">
                    <ExternalLink size={12} /> Open full Inventory Ageing for this material
                  </button>
                </div>
              )}
            </>
          ) : view === "gl" ? (
            <>
              {glErr && <div className="m-4 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="gl-error">{glErr}</div>}
              {!gl && !glErr && <div className="p-8 text-sm text-slate-400 animate-pulse text-center">Loading GL entries from SAP…</div>}
              {gl && gl.note && <div className="m-4 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="gl-note">{gl.note}</div>}
              {gl && !gl.note && gl.rows.length === 0 && <div className="p-8 text-sm text-slate-400 text-center" data-testid="gl-empty">No GL journal-entry lines found for this material in the window.</div>}
              {gl && gl.rows.length > 0 && (
                <table className="w-full text-[12px]" data-testid="gl-entries-table">
                  <thead className="sticky top-0"><tr className="border-b border-slate-200 bg-slate-50">
                    {["Posting Date", "Acct Document", "Type", "GL Account", "GL Name", "Ref Doc", "D/C"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 whitespace-nowrap">{h}</th>
                    ))}
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Qty</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Amount</th>
                  </tr></thead>
                  <tbody>
                    {gl.rows.map((g, i) => (
                      <tr key={i} onClick={() => g.acct_doc && setDocDrill(g.acct_doc)} title="Open the document's line items"
                        className={`border-b border-slate-100 hover:bg-slate-50 ${g.acct_doc ? "cursor-pointer" : ""}`}
                        data-testid={`gl-entry-row-${i}`}>
                        <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtD(g.date)}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-[#B01E24]">{g.acct_doc}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-slate-600">{g.doc_type}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">{g.gl_account}</td>
                        <td className="px-3 py-1.5 text-slate-500 max-w-[160px] truncate">{g.gl_account_name || "—"}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{g.ref_doc || "—"}</td>
                        <td className={`px-3 py-1.5 text-[11px] ${g.dc === "Credit" ? "text-amber-700" : "text-emerald-700"}`}>{g.dc}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-600">{g.qty ? g.qty.toLocaleString("en-IN") : "—"}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-700">{formatINR(g.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              {err && <div className="m-4 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="drill-error">{err}</div>}
              {!d && !err && <div className="p-8 text-sm text-slate-400 animate-pulse text-center">Loading movements…</div>}
              {d && d.note && <div className="mx-4 mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">{d.note}</div>}
              {d && d.rows.length === 0 && <div className="p-8 text-sm text-slate-400 text-center" data-testid="drill-empty">No movements found for this item in the window.</div>}
              {d && d.rows.length > 0 && (
            <table className="w-full text-[12px]" data-testid="drill-movements-table">
              <thead className="sticky top-0"><tr className="border-b border-slate-200 bg-slate-50">
                {["Posting Date", "Mat. Document", "Mvt", "Category", "Plant"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 whitespace-nowrap">{h}</th>
                ))}
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Qty</th>
                <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">UoM</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Value</th>
              </tr></thead>
              <tbody>
                {d.rows.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtD(m.date)}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{m.mat_doc || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">{m.mtype}</td>
                    <td className="px-3 py-1.5"><span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap">{m.category_label}</span></td>
                    <td className="px-3 py-1.5 text-slate-500">{m.plant}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${m.qty < 0 ? "text-amber-700" : "text-emerald-700"}`}>{m.qty.toLocaleString("en-IN")}</td>
                    <td className="px-2 py-1.5 text-slate-400">{m.uom}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-slate-700">{formatINR(m.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
              )}
            </>
          )}
        </div>

        {view === "mov" && d && d.rows.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs" data-testid="drill-totals">
            <span className="text-slate-500">{d.count.toLocaleString("en-IN")} movement line{d.count === 1 ? "" : "s"}{d.count > d.rows.length ? ` (showing first ${d.rows.length.toLocaleString("en-IN")})` : ""}</span>
            <span className="font-mono font-semibold text-slate-800">Net Qty {d.totals.qty.toLocaleString("en-IN")} · Net Value {formatINR(d.totals.value)}</span>
          </div>
        )}
        {view === "gl" && gl && gl.rows.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs" data-testid="gl-totals">
            <span className="text-slate-500">{gl.count.toLocaleString("en-IN")} GL line{gl.count === 1 ? "" : "s"}{gl.count > gl.rows.length ? ` (showing first ${gl.rows.length.toLocaleString("en-IN")})` : ""}</span>
            <span className="font-mono font-semibold text-slate-800">Net Amount {formatINR(gl.totals.amount)}</span>
          </div>
        )}
        {docDrill && <DocLinesDialog docNo={docDrill} report="stock" onClose={() => setDocDrill(null)} />}
      </div>
    </div>
  );
}
